import { useLocation } from 'react-router-dom';

import { BootSkeleton } from '@/components/BootSkeleton';
import { isTermsExemptPath } from '@/core/router/terms-exempt-paths';
import { TermsAcceptancePage } from '@/features/terms';

import { useTermsAcceptance } from './terms-acceptance-context';

import type { PropsWithChildren, ReactElement } from 'react';

/**
 * Blocks the entire router until the signed-in user has accepted the Terms of
 * Service (ADR-0497).
 *
 * WHY IT IS A PROVIDER AND NOT A ROUTE. The obvious implementation is a wrapper
 * around `<AppShell/>` in `routes.tsx`, or a redirect to a `/terms` route. Both
 * are wrong for this requirement:
 *
 *   - Wrapping AppShell covers in-shell pages only, and misses the four
 *     authenticated out-of-shell interstitials (`/checkout`, `/cli-auth`,
 *     `/invite/accept`, `/onboarding/create-workspace`) — so "no matter what page
 *     they are on" would be false for exactly the pages that mint credentials and
 *     take payment.
 *   - A redirect changes the URL, pushes history, and has to remember and restore
 *     where the user was going. Every one of those is a chance to lose their
 *     destination or leave a `/terms` entry in the back button. Rendering in place
 *     leaves the URL untouched, so when the gate opens the router mounts the page
 *     they originally asked for with no navigation at all.
 *
 * WHY IT DOES NOT FLICKER. Three properties, in the order they matter:
 *
 *   1. It never renders children before it knows. The unknown state renders a
 *      placeholder, so the app is never painted and then yanked away — which is
 *      the worst version of this bug and the one an "optimistic" gate produces.
 *   2. That placeholder is the SAME `BootSkeleton` the workspace hydrator above it
 *      renders, so when React swaps one gate's element for the other's, the
 *      painted pixels do not change.
 *   3. Accepting flips the state from the mutation's own result (written into the
 *      query's cache entry by the provider), so the interstitial gives way to the
 *      dashboard directly, with no refetch and no third loading frame.
 *
 * WHY THERE IS NO LAYOUT JUMP. The gate REPLACES the tree rather than overlaying
 * it, so there is no `position: fixed` scrim to offset, no scroll lock, no
 * background that keeps scrolling underneath, and no scrollbar appearing or
 * disappearing as the overlay mounts — the page beneath was never rendered. That
 * also means none of `ui-design.md` § 11's overlay rules apply: this is a full
 * screen, not a modal, and treating it as a modal is what would introduce the
 * jump.
 */
export function TermsGate({ children }: PropsWithChildren): ReactElement {
  const { status } = useTermsAcceptance();
  const { pathname } = useLocation();

  // Public routes bypass the gate entirely — checked FIRST so an unsubscribe link
  // or a shared report never even shows the placeholder, let alone the
  // interstitial. See `terms-exempt-paths.ts` for why the list is derived.
  if (isTermsExemptPath(pathname)) return <>{children}</>;

  switch (status.kind) {
    case 'required':
      return <TermsAcceptancePage previouslyAcceptedVersion={status.previouslyAcceptedVersion} />;
    case 'unknown':
      return <BootSkeleton />;
    case 'accepted':
    case 'not-applicable':
    case 'unavailable':
      return <>{children}</>;
  }
}
